import { Box } from '@mui/material';
import {
  ConciliationDetail,
  RenderNestedComponent,
  ChildMap,
  conciliationDetailKeys as K,
  FilterByColumn,
  concDetErrorType,
} from '../../types';
import { DataGridHandler } from '../DataGridFlexSales';
import { reportConciliationDetailFilterByErrorSelector } from '../../state';
import StatusBullet from '../StatusBullet';

export interface ConciliationsDataGridContext {
  t: (key: string | string[]) => string;
  country: string;
  renderNestedComponent?: RenderNestedComponent;
  childMap?: ChildMap;
  filterByColumn?: FilterByColumn;
  conciliationMethod?: 'G' | 'D';
}

interface SimpleRule {
  code: string;
}

const AFFIX_$ = '$';
const ROW_KEY_FLEX_VENTAS = 'Flex Ventas';
const ROW_KEY_FISCAL = 'Fiscal';
const DETAIL_ROWS = [ROW_KEY_FLEX_VENTAS, ROW_KEY_FISCAL] as const;
const LABEL_COLUMN_ACCESSOR = 'error';

const CHILD_MAP: ChildMap = {
  labelColumnAccessor: LABEL_COLUMN_ACCESSOR,
  childRows: DETAIL_ROWS,
  map: {
    transactionType: [
      { rowKey: ROW_KEY_FLEX_VENTAS, accessor: K.transactionTypeFv },
      { rowKey: ROW_KEY_FISCAL, accessor: K.transactionTypeFs },
    ],
    transactionAmt: [
      { rowKey: ROW_KEY_FLEX_VENTAS, accessor: K.transactionAmtFv, prefix: AFFIX_$ },
      { rowKey: ROW_KEY_FISCAL, accessor: K.transactionAmtFs, prefix: AFFIX_$ },
    ],
    discount: [
      { rowKey: ROW_KEY_FLEX_VENTAS, accessor: K.discountOnTotalAmtFv, prefix: AFFIX_$ },
      { rowKey: ROW_KEY_FISCAL, accessor: K.discountOnTotalAmtFs, prefix: AFFIX_$ },
    ],
    fiscalId: [
      {
        rowKey: ROW_KEY_FLEX_VENTAS,
        accessor: K.fiscalId,
        /* renderCell: (value) => <KeyPreviewCell value={value} />, */
      },
    ],
    GCs: [
      {
        rowKey: ROW_KEY_FLEX_VENTAS,
        accessor: K.qtySalesFv,
        renderCell: value => (
          <>
            <strong>GCs (FV): </strong>
            {value}
          </>
        ),
      },
      {
        rowKey: ROW_KEY_FISCAL,
        accessor: K.qtySalesFs,
        renderCell: value => (
          <>
            <strong>GCs (FS): </strong>
            {value}
          </>
        ),
      },
    ],
  },
};

const filterByColumn: FilterByColumn = {
  accessor: LABEL_COLUMN_ACCESSOR,
  selector: reportConciliationDetailFilterByErrorSelector,
};

const initialContext: ConciliationsDataGridContext = {
  t: () => '',
  country: '',
  childMap: CHILD_MAP,
  filterByColumn,
};

export default (locale: Locale, activeRules: SimpleRule[] = []) => {
  const handler = new DataGridHandler<ConciliationDetail, ConciliationsDataGridContext>(
    initialContext,
    locale
  );

  handler.addColumn({
    accessor: 'store',
    type: 'string',
    title: ({ t, country }) => t([`table.columns.${country}.store`, 'table.columns.store']),
    description: ({ t }) => t('table.description.store'),
  });

  handler.addColumn({
    accessor: 'accountingDate',
    type: 'date',
    format: 'P',
    title: ({ t }) => t('table.columns.accountingDate'),
    description: ({ t }) => t('table.description.accountingDate'),
  });

  handler.addColumn({
    accessor: 'businessDateFv',
    type: 'date',
    format: 'P',
    title: ({ t }) => t('conciliationDetails.columns.businessDate'),
    description: ({ t }) => t('conciliationDetails.description.businessDate'),
    // width: { px: 180, pt: 400 },
    // renderCell: () => null,
    available: ctx => ctx.conciliationMethod !== 'G',
  });

  handler.addColumn({
    accessor: 'fiscalKey',
    type: 'string',
    title: ({ t }) => t('conciliationDetails.columns.fiscalKey'),
    description: ({ t }) => t('conciliationDetails.description.fiscalKey'),
    width: { px: 300, pt: 300 },
    available: ctx => ctx.conciliationMethod !== 'G',
    // renderCell: (_value, row ) => (
    //  <KeyPreviewCell value={row.fiscalKey} />
    // ),
    renderCell: (_value, row) => (
      <Box component="span" sx={{ pr: '4px' }}>
        {row.fiscalKey}
      </Box>
    ),
  });

  handler.addColumn({
    accessor: 'posNum',
    type: 'string',
    width: { px: 10, pt: 10 },
    title: ({ t }) => t('table.columns.pos_num'),
    description: ({ t }) => t('conciliationDetails.description.pos_num'),
  });

  handler.addColumn({
    accessor: 'error',
    type: 'string',
    title: ({ t }) => t('conciliationDetails.columns.error'),
    description: ({ t }) => t('conciliationDetails.description.error'),
    width: { px: 205, pt: 200 },
    renderCell: (_value, row) => <StatusBullet status={row.error} />,
  });

  handler.addColumn({
    accessor: 'transactionType',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.transactionType'),
    // description: ({ t }) => t('conciliationHeaders.description.transactionType'),
    renderCell: () => null,
    available: ctx => ctx.conciliationMethod !== 'G',
  });

  handler.addColumn({
    accessor: 'transactionAmt',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.transactionAmt'),
    // description: ({ t }) => t('conciliationHeaders.description.transactionAmt'),
    renderCell: () => null,
  });

  handler.addColumn({
    accessor: 'discount',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.discount'),
    // description: ({ t }) => t('conciliationHeaders.description.discount'),
    renderCell: () => null,
    available: ctx => ctx.conciliationMethod !== 'G',
  });

  const showFiscalId = activeRules.some(r => r.code === concDetErrorType.DIFFERENTS_CNPJ);

  if (showFiscalId) {
    handler.addColumn({
      accessor: 'fiscalId',
      type: 'string',
      title: ({ t }) => t('conciliationDetails.columns.fiscalId'),
      description: ({ t }) => t('conciliationDetails.description.fiscalId'),
      renderCell: () => null,
      available: ctx => ctx.conciliationMethod !== 'G',
    });
  }

  handler.addColumn({
    accessor: 'xjust',
    type: 'string',
    title: ({ t }) => t('conciliationDetails.columns.xjust'),
    width: { px: 450, pt: 400 },
    // description: ({ t }) => t('table.description.xjust'),
    available: ctx => ctx.conciliationMethod !== 'G',
  });

  // GCs Column only for PA
  handler.addColumn({
    accessor: 'GCs',
    type: 'string',
    title: ({ t }) => t('conciliationDetails.columns.gcs'),
    width: { px: 450, pt: 400 },
    // description: ({ t }) => t('table.description.xjust'),
    available: ctx => ctx.conciliationMethod === 'G',
  });
  // ---- GCs Fiscal and FV Columns (only for PANAMA)
  handler.addColumn({
    name: 'qtySalesFv',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.qtySalesFv'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number((row as any)[K.qtySalesFv] ?? 0),
  });

  handler.addColumn({
    name: 'qtySalesFs',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.qtySalesFs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number((row as any)[K.qtySalesFs] ?? 0),
  });

  // Columns to Export

  // ---- Transaction
  handler.addColumn({
    name: 'transactionType__fv',
    type: 'string',
    title: ({ t }) => t('conciliationDetails.columns.transactionTypeFv'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    valueGetter: row => (row as any)[K.transactionTypeFv] ?? '',
  });

  handler.addColumn({
    name: 'transactionType__fs',
    type: 'string',
    title: ({ t }) => t('conciliationDetails.columns.transactionTypeFs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    valueGetter: row => (row as any)[K.transactionTypeFs] ?? '',
  });

  // ---- Transaction Amt FV
  handler.addColumn({
    name: 'transactionAmt__fv',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.transactionAmtFv'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number((row as any)[K.transactionAmtFv] ?? 0),
  });

  handler.addColumn({
    name: 'transactionAmt__fs',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.transactionAmtFs'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number((row as any)[K.transactionAmtFs] ?? 0),
  });

  // ---- Discount
  handler.addColumn({
    name: 'discount__fv',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.discountFV'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number((row as any)[K.discountOnTotalAmtFv] ?? 0),
  });

  handler.addColumn({
    name: 'discount__fs',
    type: 'number',
    title: ({ t }) => t('conciliationDetails.columns.discountFS'),
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    align: 'right',
    valueGetter: row => Number((row as any)[K.discountOnTotalAmtFs] ?? 0),
  });
  // Fiscal ID

  handler.addColumn({
    name: 'fiscalId__export',
    type: 'string',
    title: ({ t }) => t('conciliationDetails.columns.fiscalId'),
    width: { px: 450, pt: 150 },
    defaultHidden: true,
    visibilityTogglingDisabled: true,
    valueGetter: row => (row as any).fiscalId ?? '',
  });
  // Justification
  handler.addColumn({
    name: 'xjust__export',
    type: 'string',
    title: ({ t }) => t('conciliationDetails.columns.xjust'),
    width: { px: 450, pt: 400 },
    defaultHidden: true,
    visibilityTogglingDisabled: true,

    valueGetter: row => (row as any).xjust ?? '',
  });

  handler.setExportColumnPredicate(
    col =>
      ['store', 'accountingDate', 'businessDateFv', 'fiscalKey', 'posNum', 'error'].includes(
        col.id
      ) || col.id.includes('__')
  );

  const PA_COLUMNS = [
    'store',
    'accountingDate',
    'posNum',
    'error',
    'transactionAmt__fv',
    'transactionAmt__fs',
    'qtySalesFv',
    'qtySalesFs',
  ];

  handler.setExportColumnPredicate((col, ctx) => {
    if (ctx.conciliationMethod === 'G') return PA_COLUMNS.includes(col.id as any);
    return (
      ['store', 'accountingDate', 'businessDateFv', 'fiscalKey', 'posNum', 'error'].includes(
        col.id
      ) || col.id.includes('__')
    );
  });

  handler.setExportColumnSorter((a, b, ctx) => {
    if (ctx.conciliationMethod !== 'G') return 0;

    const getOrder = (id: string) => {
      const index = PA_COLUMNS.indexOf(id as any);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };

    return getOrder(a.id) - getOrder(b.id);
  });
  return handler;
};
